





























































import { CreateElement } from "vue";
import { Component, Vue, Prop } from "vue-property-decorator";

type IPaginationBlock = {
  type?: string;
  page?: number;
  content?: { render: (h: CreateElement) => any };
  class?: string | object;
  disabled?: boolean;
  on?: {
    click: Function | Function[];
  };
};

@Component
export default class UPagination extends Vue {
  // Props
  @Prop({ required: true })
  public total!: number;

  @Prop({ default: 10 })
  public perPage!: number;

  @Prop({ default: 10 })
  public current!: number;

  @Prop({ default: 1 })
  public beforeCurrent!: number;

  @Prop({ default: 1 })
  public afterCurrent!: number;

  @Prop({ default: false })
  public readonly compact!: boolean;

  @Prop({ default: false })
  public disabled!: boolean;

  // Data
  public localPerPage = this.perPage;

  // Computed
  public get numberOfPages() {
    return Math.ceil(this.total / this.localPerPage);
  }

  public get shownRange() {
    return {
      first: this.current * this.localPerPage - this.localPerPage + 1,
      last:
        this.current * this.localPerPage > this.total
          ? this.total
          : this.current * this.localPerPage
    };
  }

  public get pageCount() {
    return this.pages.length;
  }

  /**
   * Check if first page button should be visible.
   */
  public get hasFirst() {
    return this.current >= 2 + this.beforeCurrent;
  }

  /**
   * Check if first ellipsis should be visible.
   */
  public get hasFirstEllipsis() {
    return this.current >= this.beforeCurrent + 4;
  }

  /**
   * Check if last page button should be visible.
   */
  public get hasLast() {
    return this.current <= this.pageCount - (1 + this.afterCurrent);
  }

  /**
   * Check if last ellipsis should be visible.
   */
  public get hasLastEllipsis() {
    return this.current < this.pageCount - (2 + this.afterCurrent);
  }

  /**
   * Get near pages, 1 before and 1 after the current.
   */
  public get pagesInRange() {
    if (this.compact) return [];

    let left = Math.max(1, this.current - this.beforeCurrent);
    if (left - 1 === 2) {
      left--; // Do not show the ellipsis if there is only one to hide
    }
    let right = Math.min(this.current + this.afterCurrent, this.pageCount);
    if (this.pageCount - right === 2) {
      right++; // Do not show the ellipsis if there is only one to hide
    }

    const pages: IPaginationBlock[] = [];
    for (let i = left; i <= right; i++) {
      const page = this.$_.get(this.pages, `${i - 1}`);
      if (page) pages.push(page);
    }
    return pages;
  }

  public get summaryComponent() {
    return {
      render: (h: CreateElement) =>
        h(
          "p",
          {
            class: {
              "is-size-7 has-text-grey has-overflow-ellipsis-right": true,
              "has-text-centered": this.compact
            }
          },
          [
            this.$t(
              this.compact
                ? "_sentence.pagination.mobile_summary"
                : "_sentence.pagination.tablet_summary",
              {
                first: this.shownRange.first,
                last: this.shownRange.last,
                total: this.total
              }
            ).toString()
          ]
        )
    };
  }

  public get startButton(): IPaginationBlock {
    const disabled =
      this.current <= (this.firstPage?.page || 0) || this.disabled;
    return {
      class: {
        "u-p8n-start": true,
        "is-light": disabled,
        "is-paddingless": true
      },
      content: this.renderSVG(
        "M0 424V88c0-13.3 10.7-24 24-24h24c13.3 0 24 10.7 24 24v336c0 13.3-10.7 24-24 24H24c-13.3 0-24-10.7-24-24zm280.5-66.4L214.9 292H424c13.3 0 24-10.7 24-24v-24c0-13.3-10.7-24-24-24H214.9l65.6-65.6c9.4-9.4 9.4-24.6 0-33.9l-17-17c-9.4-9.4-24.6-9.4-33.9 0L94.1 239c-9.4 9.4-9.4 24.6 0 33.9l135.5 135.5c9.4 9.4 24.6 9.4 33.9 0l17-17c9.4-9.3 9.4-24.5 0-33.8z"
      ),
      disabled,
      on: {
        click: () => {
          if (!disabled) this.onPageChange(1);
        }
      }
    };
  }

  public get endButton(): IPaginationBlock {
    const disabled = this.current >= this.pageCount || this.disabled;
    return {
      class: {
        "u-p8n-end": true,
        "is-light": disabled,
        "is-paddingless": true
      },
      content: this.renderSVG(
        "M448 88v336c0 13.3-10.7 24-24 24h-24c-13.3 0-24-10.7-24-24V88c0-13.3 10.7-24 24-24h24c13.3 0 24 10.7 24 24zm-280.5 66.4l65.6 65.6H24c-13.3 0-24 10.7-24 24v24c0 13.3 10.7 24 24 24h209.1l-65.6 65.6c-9.4 9.4-9.4 24.6 0 33.9l17 17c9.4 9.4 24.6 9.4 33.9 0L353.9 273c9.4-9.4 9.4-24.6 0-33.9L218.4 103.5c-9.4-9.4-24.6-9.4-33.9 0l-17 17c-9.4 9.4-9.4 24.6 0 33.9z"
      ),
      disabled,
      on: {
        click: () => {
          if (!disabled) this.onPageChange(this.pageCount);
        }
      }
    };
  }

  public get goLeftButton(): IPaginationBlock {
    const disabled =
      this.current <= (this.firstPage?.page || 0) || this.disabled;
    return {
      class: { "u-p8n-previous": true, "is-light": disabled },
      content: this.renderSVG(
        "M257.5 445.1l-22.2 22.2c-9.4 9.4-24.6 9.4-33.9 0L7 273c-9.4-9.4-9.4-24.6 0-33.9L201.4 44.7c9.4-9.4 24.6-9.4 33.9 0l22.2 22.2c9.5 9.5 9.3 25-.4 34.3L136.6 216H424c13.3 0 24 10.7 24 24v32c0 13.3-10.7 24-24 24H136.6l120.5 114.8c9.8 9.3 10 24.8.4 34.3z"
      ),
      disabled,
      on: {
        click: () => {
          if (!disabled) this.onPageChange(this.current - 1);
        }
      }
    };
  }

  public get goRightButton(): IPaginationBlock {
    const disabled = this.current >= this.pageCount || this.disabled;
    return {
      class: { "u-p8n-next": true, "is-light": disabled },
      content: this.renderSVG(
        "M190.5 66.9l22.2-22.2c9.4-9.4 24.6-9.4 33.9 0L441 239c9.4 9.4 9.4 24.6 0 33.9L246.6 467.3c-9.4 9.4-24.6 9.4-33.9 0l-22.2-22.2c-9.5-9.5-9.3-25 .4-34.3L311.4 296H24c-13.3 0-24-10.7-24-24v-32c0-13.3 10.7-24 24-24h287.4L190.9 101.2c-9.8-9.3-10-24.8-.4-34.3z"
      ),
      disabled,
      on: {
        click: () => {
          if (!disabled) this.onPageChange(this.current + 1);
        }
      }
    };
  }

  public get ellipsis(): IPaginationBlock {
    return {
      type: "is-white",
      class: "u-p8n-hellip",
      disabled: true
    };
  }

  public get firstPage(): IPaginationBlock {
    return this.$_.head(this.pages) as IPaginationBlock;
  }

  public get lastPage(): IPaginationBlock {
    return this.$_.last(this.pages) as IPaginationBlock;
  }

  public get blocks(): any[] {
    //  Compact
    if (this.compact)
      return this.$_.compact([
        this.renderBlockComponent(this.startButton),
        this.renderBlockComponent(this.goLeftButton),
        this.summaryComponent,
        this.renderBlockComponent(this.goRightButton),
        this.renderBlockComponent(this.endButton)
      ]);

    return this.$_.compact([
      this.renderBlockComponent(this.goLeftButton),
      this.hasFirst ? this.renderBlockComponent(this.firstPage) : null,
      this.hasFirstEllipsis ? this.renderBlockComponent(this.ellipsis) : null,
      ...this.pagesInRange.map(i => this.renderBlockComponent(i)),
      this.hasLastEllipsis ? this.renderBlockComponent(this.ellipsis) : null,
      this.hasLast ? this.renderBlockComponent(this.lastPage) : null,
      this.renderBlockComponent(this.goRightButton)
    ]);
  }

  public get pages(): IPaginationBlock[] {
    return this.$_.range(1, this.numberOfPages + 1).map(page => {
      return {
        page: page,
        class: { "u-p8n-number": true, "is-info": page === this.current },
        on: {
          click: () => this.onPageChange(page)
        }
      };
    });
  }

  // Methods
  public onPageChange(value: number) {
    this.$emit("change-page", value);
  }

  public onPerPageChange(value: number) {
    this.$emit("change-per-page", value);
  }

  public renderBlockComponent(block: IPaginationBlock) {
    return {
      render: (h: CreateElement) =>
        h(
          "a",
          {
            attrs: {
              role: "button",
              disabled: block.disabled
            },
            class: ["button", block.class],
            props: { disabled: block.disabled },
            on: block.on
          },
          [block.content ? block.content.render(h) : block.page]
        )
    };
  }

  public renderSVG(d) {
    return {
      render: (h: CreateElement) =>
        h(
          "svg",
          {
            attrs: {
              xmlns: "http://www.w3.org/2000/svg",
              viewBox: "0 0 448 512",
              width: "1rem",
              height: "1rem"
            }
          },
          [
            h("path", {
              attrs: {
                fill: "currentColor",
                d
              }
            })
          ]
        )
    };
  }
}
